import { Capacitor } from "@capacitor/core";
import { Health } from "@capgo/capacitor-health";
import { db } from "./firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

// ── Workout type mapping ──────────────────────────────────────────────────────

export function mapWorkoutType(workoutType) {
  const t = String(workoutType || "").toLowerCase();
  if (t === "running" || t === "runningtreadmill") return "Running";
  if (t === "walking") return "Walking";
  if (t === "cycling" || t === "bikingstationary") return "Biking";
  if (t === "rowing" || t === "rowingmachine") return "Rowing";
  if (t === "swimming" || t === "swimmingpool" || t === "swimmingopenwater") return "Swimming";
  if (t === "elliptical") return "Elliptical";
  if (t === "stairclimbing" || t === "stairclimbingmachine" || t === "stairs") return "Stair Climber";
  if (t === "highintensityintervaltraining" || t === "mixedcardio") return "HIIT";
  if (t === "strengthtraining" || t === "traditionalstrengthtraining" || t === "functionalstrengthtraining" || t === "weightlifting") return "Strength Training";
  if (t === "yoga") return "Yoga";
  if (t === "hiking") return "Hiking";
  if (t === "jumprope" || t === "jumpingjack") return "Jump Rope";
  if (t === "pilates") return "Pilates";
  if (t === "dance" || t === "cardiodance" || t === "socialdance") return "Dance";
  if (t === "crosstraining" || t === "bootcamp") return "Cross Training";
  if (t === "other" || !workoutType) return "Workout";
  // Convert PascalCase to Title Case
  return String(workoutType).replace(/([A-Z])/g, " $1").trim().replace(/^./, c => c.toUpperCase());
}

// ── Health sync preferences (stored in users/{uid}.healthSync) ───────────────

export async function getHealthSyncPrefs(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.data()?.healthSync || { workouts: false, autoSideQuests: false };
  } catch {
    return { workouts: false, autoSideQuests: false };
  }
}

export async function saveHealthSyncPrefs(uid, prefs) {
  await setDoc(doc(db, "users", uid), { healthSync: prefs }, { merge: true });
}

// ── Permissions ───────────────────────────────────────────────────────────────

export async function requestWorkoutsPermission() {
  try {
    await Health.requestAuthorization({ read: ["workouts"], write: [] });
    return { granted: true };
  } catch (err) {
    return { granted: false, error: err?.message || "Permission request failed." };
  }
}

// ── Read today's workouts ─────────────────────────────────────────────────────

export async function readTodayWorkouts() {
  if (!Capacitor.isNativePlatform()) return [];
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const now = new Date();
  try {
    const result = await Health.queryWorkouts({
      startDate: todayStart.toISOString(),
      endDate: now.toISOString(),
      limit: 50,
      ascending: true,
    });
    return (result.workouts || []).map((w) => ({
      type: mapWorkoutType(w.workoutType),
      workoutType: w.workoutType || "other",
      durationSeconds: Math.round(Number(w.duration || 0)),
      calories: Math.round(Number(w.totalEnergyBurned || 0)),
      distanceMeters: Math.round(Number(w.totalDistance || 0)),
      startDate: w.startDate || null,
      endDate: w.endDate || null,
      sourceName: w.sourceName || "",
      platformId: w.platformId || "",
    }));
  } catch (err) {
    console.warn("[healthSync] readTodayWorkouts:", err?.message);
    return [];
  }
}

// ── Sync workouts → cardioLogs ────────────────────────────────────────────────

export async function syncWorkoutsToCardioLog(uid, workouts) {
  const today = new Date().toISOString().slice(0, 10);

  const q = query(
    collection(db, "cardioLogs"),
    where("uid", "==", uid),
    where("date", "==", today),
    where("source", "==", "health"),
  );
  const existingSnap = await getDocs(q);
  const existingPlatformIds = new Set(
    existingSnap.docs.map((d) => d.data().platformId).filter(Boolean)
  );

  const added = [];
  for (const workout of workouts) {
    if (workout.durationSeconds < 60) continue;
    if (workout.platformId && existingPlatformIds.has(workout.platformId)) continue;

    const entry = {
      uid,
      date: today,
      type: workout.type,
      durationSeconds: workout.durationSeconds,
      calories: workout.calories,
      source: "health",
      platformId: workout.platformId || null,
      healthSource: workout.sourceName || "",
      createdAt: serverTimestamp(),
    };

    await addDoc(collection(db, "cardioLogs"), entry);
    added.push(entry);
  }

  return added;
}

// ── Side quest auto-complete ──────────────────────────────────────────────────

const WORKOUT_KEYWORDS = [
  "cardio", "workout", "exercise", "run", "walk", "bike", "gym",
  "train", "swim", "row", "lift", "steps", "active", "move",
  "hike", "yoga", "sweat", "elliptical", "stair", "class",
];

export function questMatchesWorkout(questTitle) {
  const title = String(questTitle || "").toLowerCase();
  return WORKOUT_KEYWORDS.some((kw) => title.includes(kw));
}

// ── Full sync orchestration ───────────────────────────────────────────────────

/**
 * Run full health sync. Returns { workoutsAdded, questsToComplete }.
 * Callers handle actual side quest completion (needs App.jsx state).
 */
export async function runHealthSync(uid, prefs, activeSideQuests = []) {
  if (!Capacitor.isNativePlatform()) return { workoutsAdded: [], questsToComplete: [] };

  const result = { workoutsAdded: [], questsToComplete: [] };

  if (prefs.workouts) {
    try {
      await requestWorkoutsPermission();
      const workouts = await readTodayWorkouts();
      if (workouts.length > 0) {
        result.workoutsAdded = await syncWorkoutsToCardioLog(uid, workouts);

        if (prefs.autoSideQuests && result.workoutsAdded.length > 0) {
          if (prefs.autoSideQuestTitle) {
            result.questsToComplete = activeSideQuests
              .filter((q) => q.title === prefs.autoSideQuestTitle)
              .map((q) => q.title);
          } else {
            result.questsToComplete = activeSideQuests
              .filter((q) => questMatchesWorkout(q.title))
              .map((q) => q.title);
          }
        }
      }
    } catch (err) {
      console.warn("[healthSync] workout sync failed:", err?.message);
    }
  }

  return result;
}
